/* eslint-disable no-console */
import { ContentFooter } from "@app/components/core/ContentFooter/ContentFooter"
import { ContentHeader } from "@app/components/core/ContentHeader/ContentHeader"
import RetryError from "@app/components/core/RetryError"
import { SelectionGroup } from "@app/components/core/SelectionGroup/SelectionGroup"
import LoadingSelectorPage from "@app/components/loading/LoadingSelectorPage"
import { useCustomNavigation } from "@app/components/navigation/NavigationHandler"
import OrderContentBody from "@app/components/order/OrderContentBody/OrderContentBody"
import { PersonTypeSelector } from "@app/components/order/PersonTypeSelector/PersonTypeSelector"
import OrderSubPage from "@app/components/pageTypes/OrderSubPage/OrderSubPage"
import { OrderAnimationTypes } from "@app/components/routing/AnimatedOrderRoute/AnimatedOrderRoute"
import { AnimationTypes } from "@app/components/routing/AnimatedRoute/AnimatedRoute"
import { useRaiseError } from "@app/store/actions/errorActions"
import { addVisitorKind, removeVisitorKind } from "@app/store/actions/orderActions"
import { useGetStrings } from "@app/store/service/cmsService"
import { useCheckVisitorKindRestrictions, useGetVisitorKinds } from "@app/store/service/connectorService"
import { appStore } from "@app/store/store"
import { OrderErrors } from "@app/utils/constants"
import { useDelayLoading } from "@app/utils/hooks/useDelayLoading"
import { selectVisitorCount } from "@app/utils/selectors"
import tracking from "@app/utils/tracking"
import { ResponseError, useSelector } from "@tecreation/js-state-react"
import { useCallback, useEffect } from "react"

export default function PersonTypesPage() {
	const { push } = useCustomNavigation()
	const visitorKinds = useSelector(state => state.order.visitorKinds)
	const locationId = useSelector(state => state.core.locationId)
	const zoneId = useSelector(state => state.core.zoneId)
	const visitorCount = useSelector(selectVisitorCount)
	const visitorKindPrice = useSelector("order.visitorKindPrice")
	const htgGuestData = useSelector("core.htgGuestData")
	const priceActions = useSelector("order.priceActions")
	const [{ isLoading }] = useCheckVisitorKindRestrictions()
	const { data: visitorKindTypes, isFetching: __loading, error: visitorKindError, refetch: retryVisitorKinds } = useGetVisitorKinds({ locationId, zoneId })
	const {
		data: { personTypesTitle, buttonTicketSelection, proceedToTicketSelectionLabel, guestSelectionLabel },
	} = useGetStrings()

	const loading = useDelayLoading(__loading)

	const raiseError = useRaiseError()

	const validate = useCallback(async () => {
		try {
			push("/order/date-selection", OrderAnimationTypes.fullSlide)

			tracking.personTypes()
		} catch (e) {
			if (
				e instanceof ResponseError &&
				e.response.data &&
				(e.response.data.errorMessage.startsWith("wrn.VisitorKind.NoAccess") ||
					e.response.data.errorMessage.startsWith("wrn.VisitorKindRule.RequiredVisitorKind"))
			) {
				raiseError(e, {
					"${type}": e.response.data.errorMessage.split(";")[1],
					"${replacement}": e.response.data.errorMessage.split(";")[1],
				})
			} else {
				raiseError(e, undefined, false, true)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visitorCount, visitorKinds, priceActions])

	useEffect(() => {
		appStore.set(state => ({
			order: {
				...state.order,
				selectedDay: undefined,
				selectedTb: undefined,
				selectedTbId: undefined,
				selectedTbKind: undefined,
				selectedTs: undefined,
			},
		}))
	}, [])

	useEffect(() => {
		if (visitorKindError) {
			raiseError(visitorKindError, undefined, false, true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visitorKindError])

	useEffect(() => {
		if (visitorKindTypes && htgGuestData && htgGuestData.guestcards) {
			if (Object.keys(visitorKinds).length == 0) {
				function _calculateAge(birthday) {
					var ageDifMs = Date.now() - birthday.getTime()
					var ageDate = new Date(ageDifMs)
					return Math.abs(ageDate.getUTCFullYear() - 1970)
				}

				const isAdult = age => age >= 16
				const isChild = age => age > 3 && age <= 16
				const getVisitorKindId = age => (isAdult(age) ? 1 : isChild(age) ? 2 : 3)

				//apply visitorKinds from htgGuestData
				for (let card of htgGuestData.guestcards) {
					let birthdate = new Date(card.b)
					let age = _calculateAge(birthdate)
					let vk = visitorKindTypes.find(e => e.visitorKindId === getVisitorKindId(age))
					if (vk) {
						// its possible that we have an guest that doesn't match to a existing type. then we ignore it
						let refHtg = appStore.get("settings.htgGuestCardCode") + "" + (htgGuestData.guestcards.indexOf(card) + 1)
						//only apply ref when existing
						if (!priceActions.find(e => e.priceActionCode === refHtg)) {
							refHtg = undefined
						}
						addVisitorKind(vk, refHtg)
					} else {
						console.group("can not find visitor kind for age " + age)
						console.log(card)
						console.groupEnd()
					}
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [htgGuestData, visitorKindTypes])

	return (
		<OrderSubPage>
			<ContentHeader canGoBack colorStyle="white" animation={AnimationTypes.slide} process={1} ariaLabel={guestSelectionLabel}>
				{personTypesTitle}
			</ContentHeader>

			<OrderContentBody className="flex flex-column pb-4" route="person-types">
				<LoadingSelectorPage show={loading} showTitle={false} length={3} />

				<RetryError show={visitorKindError} loading={__loading} onRetry={retryVisitorKinds} />

				<SelectionGroup>
					{visitorKindTypes &&
						visitorKindTypes.map((visitorKind, index) => {
							if (visitorKind.minPrice === undefined) {
								return null
							}
							return (
								<PersonTypeSelector
									count={visitorKinds[visitorKind.visitorKindId] ? visitorKinds[visitorKind.visitorKindId].cnt : 0}
									text={visitorKind.title}
									subText={visitorKind.subTitle}
									rateText={visitorKind?.rateTitle}
									key={index}
									onAdd={() => addVisitorKind(visitorKind)}
									onRemove={() => removeVisitorKind(visitorKind)}
									notification={visitorKind.memo}
								/>
							)
						})}
				</SelectionGroup>
			</OrderContentBody>
			<ContentFooter
				process={1}
				price={visitorKindPrice}
				buttonProps={{
					onNext: validate,
					text: buttonTicketSelection,
					disabled: visitorCount === 0,
					isLoading: isLoading,
					disabledReason: OrderErrors.noSelectedPersons,
				}}
				ariaLabel={proceedToTicketSelectionLabel}
			/>
		</OrderSubPage>
	)
}
